import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * The platform landing's furniture, in the same hand as the athlete app's front page: slabs a step
 * above the page, soft washes mixed from the theme's `--primary`, one pill to press, and titles set
 * light and tight. Retheme the app and every tint follows.
 */

/** Spread onto the page wrapper. Every tint on the landing is mixed from these. */
export const landingVars = {
  "--site-wash": "color-mix(in oklch, var(--primary) 13%, var(--background))",
  "--site-fog": "color-mix(in oklch, var(--primary) 30%, var(--background))",
  "--site-ink": "color-mix(in oklch, var(--primary) 70%, var(--foreground))",
  "--site-tint": "color-mix(in oklch, var(--primary) 5%, var(--muted))",
  "--site-well": "color-mix(in oklch, var(--foreground) 5%, var(--card))",
  "--site-glow": "color-mix(in oklch, var(--primary) 22%, transparent)",
} as CSSProperties;

/** The fog that rises from the bottom edge of the head card. */
export const fogClass =
  "after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:-z-10 after:h-3/5 after:bg-linear-to-t after:from-[var(--site-fog)] after:to-transparent after:opacity-80";

export const sectionTitle = "text-[clamp(1.75rem,3.6vw,2.875rem)] leading-[1.05] font-medium tracking-[-0.04em]";

/** The bigger title of a section that makes one point, rather than lists things. */
export const displayTitle =
  "text-[clamp(2.25rem,4.6vw,4rem)] leading-none font-medium tracking-[-0.045em] text-balance";

export const glass =
  "border border-white/10 bg-[color-mix(in_oklch,var(--page-background)_62%,transparent)] backdrop-blur-xl";

export const ghostButton =
  "inline-flex h-10 items-center gap-1.5 rounded-full border border-border bg-background px-[18px] text-sm font-medium whitespace-nowrap transition-colors hover:bg-muted";

/** A drawing's surface on a tinted slab: the app's own card, lifted. */
export const panel =
  "rounded-[18px] bg-background shadow-[0_14px_30px_color-mix(in_oklch,var(--primary)_14%,transparent)]";

/** The words in a title that carry the accent. */
export const Accent = ({ children }: { children: ReactNode }) => <span className="text-primary">{children}</span>;

/** The small label over a title: a lit dot and a few uppercase words. */
export const Eyebrow = ({ children, className }: { children: ReactNode; className?: string }) => (
  <span
    className={cn(
      "inline-flex items-center gap-2 text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase",
      className,
    )}
  >
    <i aria-hidden className="size-1.5 rounded-full bg-primary shadow-[0_0_12px_var(--primary)]" />
    {children}
  </span>
);

/** One band of the page. Sections share the wide column and the same breathing room above. */
export const Section = ({ id, children, className }: { id?: string; children: ReactNode; className?: string }) => (
  <section id={id} className={cn("scroll-mt-6 pt-16 md:pt-[104px]", className)}>
    {children}
  </section>
);

/**
 * A section's title on the left, with one line on the right. `as="h1"` when the section opens a
 * page of its own (/pricing, /features, /contact), so that page still has exactly one h1.
 */
export const SectionHead = ({
  eyebrow,
  title,
  aside,
  as: Heading = "h2",
}: {
  eyebrow?: string;
  title: ReactNode;
  aside?: ReactNode;
  as?: "h1" | "h2";
}) => (
  <div className="mb-9 grid items-end gap-6 px-1 md:grid-cols-[1fr_auto]">
    <div>
      {eyebrow ? <Eyebrow className="mb-4">{eyebrow}</Eyebrow> : null}
      <Heading className={sectionTitle}>{title}</Heading>
    </div>
    {aside ? <div className="max-w-[340px] text-sm text-muted-foreground">{aside}</div> : null}
  </div>
);

const goTones = {
  ink: "bg-foreground text-background",
  accent: "bg-primary text-primary-foreground",
};

/** The call to action: a pill with its arrow in a circle beside it. */
export const GoLink = ({
  children,
  to,
  hash,
  tone = "ink",
  className,
}: {
  children: ReactNode;
  /** An app route, or an in-page anchor such as `#pricing`. */
  to: string;
  hash?: string;
  tone?: keyof typeof goTones;
  className?: string;
}) => {
  const content = (
    <>
      <span className={cn("inline-flex h-10 items-center rounded-full px-[18px] text-sm font-medium whitespace-nowrap", goTones[tone])}>
        {children}
      </span>
      <span
        className={cn(
          "grid size-10 shrink-0 place-items-center rounded-full transition-transform duration-200 group-hover:rotate-45",
          goTones[tone],
        )}
      >
        <ArrowUpRight aria-hidden className="size-4" />
      </span>
    </>
  );
  const classes = cn(
    "group inline-flex shrink-0 items-center gap-1 rounded-full outline-none transition-transform focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.98]",
    className,
  );

  return to.startsWith("#") ? (
    <a href={to} className={classes}>
      {content}
    </a>
  ) : (
    <Link to={to} hash={hash} className={classes}>
      {content}
    </Link>
  );
};

/** Initials on the soft wash, for the sample people in the drawings. */
export const Monogram = ({ name, className }: { name: string; className?: string }) => (
  <span
    aria-hidden
    className={cn(
      "grid shrink-0 place-items-center bg-linear-to-br from-[var(--site-wash)] to-[var(--site-fog)] font-medium tracking-[-0.03em] text-[var(--site-ink)]",
      className,
    )}
  >
    {name
      .split(/\s+/)
      .slice(0, 2)
      .map((word) => word[0]?.toUpperCase() ?? "")
      .join("")}
  </span>
);

/**
 * A point made in words on the left and shown on a tinted slab on the right. Kept for the few
 * sections whose picture is the feature itself, so the page does not turn into a zigzag.
 */
export const SplitFeature = ({
  id,
  eyebrow,
  title,
  body,
  children,
  className,
}: {
  id?: string;
  eyebrow: ReactNode;
  title: ReactNode;
  body: string;
  /** The picture, on the slab. */
  children: ReactNode;
  /** Layout of the slab's content. */
  className?: string;
}) => (
  <Section id={id}>
    <div className="grid items-center gap-8 lg:grid-cols-[0.75fr_1.25fr] lg:gap-14">
      <div className="max-w-[460px] px-1">
        <Eyebrow>{eyebrow}</Eyebrow>
        <h2 className={cn(displayTitle, "mt-5")}>{title}</h2>
        <p className="mt-5 text-lg leading-[1.55] text-muted-foreground">{body}</p>
      </div>
      <div
        className={cn(
          "relative isolate overflow-hidden rounded-[32px] bg-[var(--site-tint)] p-5 before:absolute before:inset-x-[20%] before:-bottom-3/5 before:-z-10 before:h-full before:bg-[radial-gradient(closest-side,var(--site-glow),transparent)] md:p-12",
          className,
        )}
      >
        {children}
      </div>
    </div>
  </Section>
);

/** A share of a goal as a ring that fills clockwise from the top. */
export const ProgressRing = ({ share, size, stroke }: { share: number; size: number; stroke: number }) => {
  const radius = (size - stroke) / 2;
  const length = 2 * Math.PI * radius;
  const center = size / 2;

  return (
    <svg aria-hidden viewBox={`0 0 ${size} ${size}`} width={size} height={size} className="shrink-0">
      <circle cx={center} cy={center} r={radius} fill="none" stroke="var(--site-well)" strokeWidth={stroke} />
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke="var(--primary)"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={length}
        strokeDashoffset={length * (1 - share)}
        transform={`rotate(-90 ${center} ${center})`}
      />
    </svg>
  );
};
